/* eslint-disable @typescript-eslint/no-namespace */
import { PaymentSlug } from '../payment';
import { DeliverySlug } from '../delivery';
import { BnplType } from '../bnpl';
import { TComplexRegion } from '../mnp';

type GetBoolKeys<T> = { [K in keyof T]: T[K] extends boolean | undefined ? K : never }[keyof T];

type OnlyBoolKeys<T> = Omit<T, Exclude<keyof T, GetBoolKeys<T>>>;
type OnlyNotBoolKeys<T> = Pick<T, Exclude<keyof T, GetBoolKeys<T>>>;

export const enum QpKey {
  ShopBasket = 'SHOP_BASKET',
  ShopBasketSpecialProject = 'BASKET_SPECIAL_PROJECT',
  ShopBasketTexts = 'SHOP_BASKET_TEXTS',
  ShopBasketBanner = 'SHOP_BASKET_BANNER',
  ShopBasketRecall = 'SHOP_BASKET_RECALL',
  ShopBasketFlocktoryTarrifPrefix = 'SHOP_BASKET_FLOCKTORY_TARRIF_PREFIX',
  ShopBasketConstructorSales = 'SHOP_BASKET_CONSTRUCTOR_SALES',
  ShopBasketTermsOfSale = 'SHOP_BASKET_TERMS_OF_SALE',
  ShopBasketPersonalDataConsent = 'SHOP_BASKET_PERSONAL_DATA_CONSENT',
  ShopBasketStatusPageSettings = 'SHOP_BASKET__STATUS_PAGE_SETTINGS',
  ShopBasketSpbBanks = 'SHOP_BASKET_SBP_BANKS',
  ShopTradeInContentTexts = 'SHOP_TRADE_IN_CONTENT_TEXTS',
  BasketOnlineDelivery = 'BASKET_ONLINE_DELIVERY',
  BasketMobileIdTexts = 'BASKET_MOBILEID_TEXTS',
  BasketEsiaTexts = 'BASKET_ESIA_TEXTS',
  BnplTexts = 'SHOP_BASKET_BNPL',
  SberInstallment = 'SHOP_BASKET_INSTALLMENT',
}

type PersonalConsentInfo = {
  header?: string;
  subHeader?: string;
  paragraphs?: string[];
};

export type PersonalConsent = {
  agreementInfo: PersonalConsentInfo;
  tooltipInfo: string;
  politicUrl: string;
  disclaimerInfo: PersonalConsentInfo;
};

export type TermsOfSale = {
  header?: string;
  paragraphs?: string[];
};

export type Banner = {
  regions?: number[];
  articles?: string[];
  mainCollectionIds?: number[];
  markup?: string;
};

export type Esia = {
  esiaAgreementText: string[];
  esiaMainText: string;
  isAuthEsiaVisible: boolean;
};

export type FlocktoryTariff = {
  prefix: string;
};

export type TradeIn = {
  categories: number[];
  notice: string;
  warningDeliveryHeader: string;
  warningDeliveryText: string;
  warningDeliveryButton: string;
  warningPaymentHeader: string;
  warningPaymentText: string;
  warningOkButton: string;
  warningDeleteButton: string;
  cashlessPayment: string[];
  isSBPAvailable: boolean;
};

export type Default = {
  enabled: boolean;
  default_position: boolean;
};

export type Settings = {
  delivery_slugs: string[];
  payments: string[];
};

export type RecallSettings = {
  checkbox_settings: {
    title: string;
    default: Default;
    settings: Settings;
  };
};

export type SBPBank = {
  slug: string;
  image: string;
  name: string;
};

export type Sbp = {
  banks: SBPBank[];
};

export type DeliveryInfo = {
  date: string;
  office: string;
};

export type Option = {
  TimeFrom: string;
  TimeTo: string;
  active: boolean;
  desc: string;
  name: string;
};

export type Stage = {
  date: string;
  deliveryInfo?: DeliveryInfo;
  deliveryType: string;
  desc: string;
  isActive?: boolean;
  isCompleted?: boolean;
  name: string;
  options: Option[];
  stage: string;
  statuses: string[];
};

export type OrderStatus = Stage[];

export type ConstructorSales = Record<string, string[]>;

export namespace QPInterface {
  export type QPSource =
    | 'Catalog'
    | 'brandzone_Shop'
    | 'recommended_Shop'
    | 'Shop'
    | 'b2c'
    | 'Recommend - Basket - С этим товаром часто покупают'
    | 'Recommend - Shop - Похожие товары'
    | 'Recommend - main_Shop - Советуем посмотреть'
    | 'Catalog_pre';

  export type QPMessenger = 'Viber' | 'Telegram' | 'WatsApp' | 'Skype' | 'Snapchat';

  export type QPSocialNetwork = 'VK' | 'Odnoklasniki';

  export type QPSettingMobileAccount = {
    orderSum: number;
    textView: string;
  };

  export type QPCreditBanks = {
    img: string;
    id: number;
    percent: string;
  };

  export type QPSBPErrorMessage = {
    img: string;
    title: string;
    subtitle: string;
  };

  export type QPMobileIdErrors = {
    default: {
      title: string;
      message: string;
      iconType: number;
    };
    'Service premiumInfo response not valid': {
      title: string;
      message: string;
      iconType: number;
    };
  };

  export type Label = {
    label: string;
    tooltip?: string;
  };

  export type ArmorJackName = {
    title: string;
    articles: string[];
  };

  export type TBroker = {
    enabled: boolean;
    priority: number;
    regions: number[];
  };

  export type CreditProductSettings = {
    monthCount: number;
    minPercent: string;
    isCredit: boolean;
    isPromo?: boolean;
  };

  export type IPopupContentSiteTexts = {
    totalCreditCostContent?: {
      info: string;
      totalCreditCostData: TTotalCreditCost[];
    };
    hasInstallmentWithoutPercent: boolean;
    title: string;
    units: {
      currency: string;
      term: string;
      period?: string;
    };
    terms: {
      title: string;
      from: string;
      extra: {
        description: string;
        installments: string;
        loan: string;
      };
    };
    credit: {
      title: {
        installments: string;
        installmentsWithInsurance: string;
        loan: string;
      };
      buttons?: {
        installment: string;
        loan: string;
      };
      tag: string;
      none: string;
      products: Label;
      insurance: Label;
      discount: Label;
      percent: Label;
      monthly: Label;
      total: Label;
    };
    coverage: {
      title: string;
      description: string;
      tag: string;
    };
    button: {
      installments: string;
      loan: string;
      default: string;
    };
    sidebar: {
      overpayment: string;
      buton: {
        default: string;
        credit?: string;
        installments: string;
        loan: string;
      };
    };
    note: string;
  };

  export type IServiceText = {
    service: string;
    title: string;
    subtitle: string;
    imgUrl: string;
    infoUrl: string;
    conditionsUrl?: string;
    learnMore?: string;
  };

  export type TTotalCreditCost = {
    monthCount: number;
    creditCost: string;
    installmentCost: string;
  };

  export type Source = {
    img: string;
    listImg: string;
    text: string;
  };

  export type Esim = {
    buttonCaption: string;
    caption: string;
    url: string;
  };

  export type PaymentsAndDeliveryType = {
    percent: number;
    deliveryType: DeliverySlug;
    paymentType: PaymentSlug;
  };

  export type AuthServicesScreenType = {
    title: string;
    subTitle: string;
    imgSrc: string;
    buttonText: string;
    actionName: string;
  };

  export type SplitPaymentsPeriods = {
    month: number;
    minRub: number;
    minPercent: number;
    minCartSum: number;
    maxCartSum: number;
    vendorPercent: number;
    numberOfPayments: number;
  };

  export type TDeleteInsurancesButtonType = {
    paymentTypes: string[];
  };

  export type TRequiredFormPd = Record<string, never>;

  export type PaymentPriority = {
    slug: `${PaymentSlug}`;
    priority: number;
    defaultActive: boolean;
  };

  export type WithoutPresetsTariffType = {
    dpcId?: string;
    description?: string;
    oldPrice?: string;
    price?: string;
  };

  export type QPBasketShop = {
    ctnCaptchaKey?: string;
    basketIdLifeTime: number;
    bonusArticles: string[];
    isReserveEnabled?: boolean;
    reserveErrorText: string;
    deliveryDateWithPreorderText?: string;
    authTypesToBackendAuthTypes: {
      [key: string]: string;
    };
    credit_popup: boolean;
    credit_popup_content: IPopupContentSiteTexts;
    insurances: IServiceText[];
    goBackLink: string;
    deliveryGoBackLink?: string;
    tooManySim: string;
    commonError: string;
    promocodeType: string;
    isTechnicalWorks: boolean;
    isFlatInAddressWarning: boolean;
    isEmailAdvAgreementInit: boolean;
    isShortMNP: boolean;
    isAdvCake: boolean;
    movementPriceTitle: string;
    isGAEnabled: boolean;
    isCreditOrInstallment: boolean;
    isCreditInPopup: boolean;
    isInsuranceOn: boolean;
    isEnabledChangeCtn: boolean;
    isFirstNameRequired: boolean;
    isLastNameRequired: boolean;
    isEmailRequired: boolean;
    isVisibleBasketSteps: boolean;
    isNonResidentModalsOn: boolean;
    orderCheckSettings: {
      delay?: number;
      attempts?: number;
      status?: string;
    };
    emailRequiredSettings?: Record<string, string[]>;
    firstNameRequiredSettings?: Record<string, string[]>;
    lastNameRequiredSettings?: Record<string, string[]>;
    emptyChipSettings: {
      title: string;
      subTitle: string;
      isActive: boolean;
      disabled: boolean;
      tag: string;
      price: string[];
      images: string[];
      onClick: null;
    };
    splitSettings: {
      isSplitOn: boolean;
      paymentSlug: PaymentSlug;
      periodSettings: SplitPaymentsPeriods[];
    };
    emptyChipDescriptionTexts?: {
      withInstallment: string;
      notInstallment: string;
    };
    isMainPersonDataFields: boolean;
    isEsiaInCreditPopup: boolean;
    isMobileIdInCreditPopup: boolean;
    isShowEsiaBirthplace: boolean;
    isCreditAvailableDelivery: string[];
    isCreditAvailableFastDelivery: string[];
    urlsReferer: {
      eSim: string[];
      sim: string[];
    };
    paymentCardOnlineIsDeleteCookieOnReturn: boolean;
    paymentMobileIsDeleteCookieOnReturn: boolean;
    importantInsuranceFormValues: string[];
    personDataShortFields: string[];
    deliveryTypeToText: Record<string, unknown>;
    socialTariffData: {
      caption: string;
      img: string;
      description: string;
      tariffList?: [];
    };
    creditSidebarTexts: {
      monthPayment: {
        title: string;
        subtitle: string;
      };
      percentByCredit: {
        title: string;
        subtitle: string;
        description: string;
      };
      installmentDiscount: {
        showDiscount: boolean;
        title: string;
      };
      installmentInfoText: string;
      installmentOrderInfoText: string;
    };
    creditOrInstallment: {
      title: string;
      subtitle: string;
    };
    ctnChangeModalText: {
      title: string;
      subtitle: string;
    };
    creditAlphaCard: {
      categories?: number[];
      itemPrice?: number;
      urlRequest?: string;
    };
    sbp: boolean;
    sbpStepsTitle: string;
    sbpRetrySec: number;
    sbpTimeout: number;
    sbpTitle: string;
    typErrorMessage: string;
    sbpSubtitle: string;
    isFlocktory: boolean;
    isTYPageFlocktoryBanner: boolean;
    isShowMovementPrice: boolean;
    sourceToList: Record<string, QPSource>;
    showPersonData: boolean;
    isValidateStreet: boolean;
    personDataExcludedDeliveryTypes: string[];
    appsHeading: string;
    yandexApiKey: string;
    delivery?: Record<string, string>;
    isOfferSectionShown: boolean;
    insurance_list_delivery: string[];
    insurance_list_fast_delivery: string[];
    sbpSteps: string[];
    preorderNote: string;
    sbpExtraInfo: string;
    onlinePaymentDiscount: string;
    offerLink: string;
    onlinePaymentDiscountPreorder: string;
    preorderSuccess: string;
    preorderSuccessHeading: string;
    alphaBankSectionButtonTitle: string;
    alphaBankSectionTitle: string;
    alphaBankSectionCaption: string;
    alphaBankSectionUrl: string;
    paymentProcessHead: string;
    b2bSuccess: string;
    paymentProcessMessage: string;
    paymentErrorHead: string;
    paymentErrorMessage: string;
    paymentOperator: string;
    textPaymentMobileAccountWait: string;
    textPaymentMobileAccountInprogress: string;
    textPaymentMobileAccountFail: string;
    paymentMobileAccountTimeout: number;
    paymentMobileAccountRetrySec: number;
    paymentMobileAccountRetryAttempts: number;
    paymentCardOnlineAccountTimeout: number;
    paymentCardOnlineAccountRetrySec: number;
    paymentCardOnlineAccountRetryAttempts: number;
    authServicesErrorText: string;
    abtest: {
      paymentsAndDeliveryType?: PaymentsAndDeliveryType;
    };
    isAuthEsiaVisible: boolean;
    isAuthMobileIdVisible: boolean;
    b2bSettings: {
      filterEnabled: boolean;
      regionsList: string[];
    };
    promoCaptchaMaxLength: number;
    captchaAllowedChars?: {
      digits?: boolean;
      cyrillic?: boolean;
      latin?: boolean;
    };
    defaultActiveAuthServicesTab: string;
    esiaAgreementText: string[];
    emailAgreement: {
      header: string;
      subHeader: string;
      paragraphs: string[];
    };
    apayEnabled: boolean;
    gpayEnabled: boolean;
    basketPaymentPriority: PaymentPriority[];
    paymentFromMobileAccount: {
      isMobilePaymentChecked: boolean;
      mobilePaymentAgreementText: {
        content: string;
        links: {
          name: string;
          description: string;
          link: string;
        }[];
      };
      mobilePaymentErrorMessage: string;
      operatorError: string;
      caption: string;
      enabled: boolean;
      totalAmount: number;
      totalAmountForMethod: number;
      commonError: string;
      settings: Record<string, QPSettingMobileAccount>;
    };
    isUnlimitedProductsAvailable: boolean;
    b2bSimpleModalTitle: string;
    b2bSimpleModalSubtitle: string;
    b2bMultiModalTitle: string;
    b2bMultiModalSubtitle: string;
    b2bMultiBundleModalSubtitle: string;
    b2cSimpleModalTitle: string;
    b2cSimpleModalSubtitle: string;
    detailingCostNewSubsSB: string;
    detailingCostMNPSubsSB: string;
    detailingCostNewSubsAP: string;
    detailingCostMNPSubsAP: string;
    insurancesLegalText: {
      name: string;
      links: string;
      confirm: string;
    };
    thankYouPageTexts: {
      anyStore: string;
      addressStore: string;
      courierDelivery: string;
      pvz: string;
      postamat: string;
      onlineDelivery: string;
      tradeIn: string;
      badReservedText: string;
    };
    thankYouPageWithInsuranceTexts: {
      notOwnPoint: string;
      ownPointPersonData: string;
      ownPoint: string;
    };
    thankYouPageDefaultDropdown: boolean;
    vsdaPD: {
      mobileidSlugs: string[];
      esia: boolean;
    };
    mobileIdErrors: QPMobileIdErrors;
    vsdaCredit: TBroker;
    posCredit: TBroker;
    creditCancel: {
      head: string;
      message: string;
      buttonText: string;
    };
    creditBroker: Record<string, string>;
    creditBanks: Record<string, QPCreditBanks>;
    creditProducts: CreditProductSettings[];
    servicesCreditTexts: Record<
      string,
      {
        message: string;
        image: string;
        badge: number | null;
        badgeUnit: string | null;
      }
    >;
    vsdaCreditFrameSrc: string;
    showCtnMnpWarning: boolean;
    isCreditPopupAutofill: boolean;
    isShowTradeIn?: boolean;
    isTradeInReplacementPayment: boolean;
    controlsPersonDataFormVD: {
      isSalaryField: boolean;
      isGenderField: boolean;
    };
    insuranceTexts: {
      titleInsuranceOn: string;
      descriptionInsuranceOn: string;
      descriptionInsuranceOnLink: string;
      titleInsuranceOff: string;
      descriptionInsuranceOff: string;
      imgUrlOn: string;
      imgUrlOff: string;
      insuranceModal: {
        modalTitleOff: string;
        modalDescOff: string;
        modalTitleOn: string;
        modalDescOn: string;
        modalButtonOn: string;
        modalButtonOff: string;
        recommended: string;
        modalFormTitle: string;
        modalFormSubtitle: string;
        modalFormSubtitleForDelivery: string;
        buttons: {
          send: Record<string, string>;
          sendLater: Record<string, string>;
          deleteInsurances: {
            name: string;
            deliveryTypes: Record<string, TDeleteInsurancesButtonType>;
          };
          sendVsDa?: Record<string, string>;
        };
      };
      phoneNumberSubtitle: string;
      buttons: {
        send: Record<string, string>;
        sendLater: Record<string, string>;
        deleteInsurances: {
          name: string;
          deliveryTypes: string[];
        };
        sendVsDa?: Record<string, string>;
      };
      personDataVsDaTexts: {
        modalFormTitle: string;
        modalFormSubtitle: string;
        legalVsDaText: string;
      };
    };
    agreementPersonalDataDefaultState?: boolean;
    agrrementPersonalDataTextOnline: string;
    agrrementPersonalDataText: string;
    agrrementPersonalDataTextMobileID: string;
    constructorNew: {
      mnpCost: number;
      connectionCost: number;
      connectionCostByRegion?: Record<string, number>;
      servicesToHide: {
        up1: string[];
        up2: string[];
        up3: string[];
        up4: string[];
        up5: string[];
      };
      sources: {
        up1: Source;
        up2: Source;
        up3: Source;
        up4: Source;
        up5: Source;
        'up1-2': Source;
        'up2-2': Source;
        'plan-b': Source;
        'internet-live': Source;
        'dlya-umnih-ustroystv': Source;
        'dlya-modemov-i-routerov': Source;
        semeyniy: Source;
        hit: Source;
        'dlya-planshetov': Source;
        'dlya-avtomobiley': Source;
        standart: Source;
        'tvoya-cena': Source;
        'dlya-pervogo-smartfona': Source;
        'dlya-detskih-chasov': Source;
        'dlya-knopochnogo-telefona': Source;
        'dlya-doma': Source;
      };
      socToservicesIcon: Record<string, string>;
      socToservicesName: Record<string, string>;
    };
    esimData: Esim;
    errorType: {
      noRemains: [string, string];
      changedDate: [string, string];
    };
    displayShortName: string;
    insurancesDetailText: string;
    isInsuranceFormForVD: boolean;
    authServicesScreen: {
      esia: AuthServicesScreenType;
      mobileId: AuthServicesScreenType;
      defaultTxt: AuthServicesScreenType;
    };
    spBannerButtonTitle?: string;
    spBannerCaption?: string;
    spBannerTitle?: string;
    spBannerUrl?: string;
    spBannerImgUrl?: string;
    spBannerEnabled: boolean;
    bnpl: {
      isShown: boolean;
      matchingPaymentSlugWithBnplSlug: Partial<Record<PaymentSlug, BnplType>>;
    };
    isInsuranceFastDelete: {
      isForOrderStep: boolean;
      isForDeliveryStep: boolean;
    };
    paymentGroups: {
      receiptTakeAwayGroup: ['cash', 'card-self-delivery'];
      receiptCourierGroup: ['cash', 'card-courier'];
      cardOnlineGroup: ['card-online'];
      sbpGroup: ['SBP'];
      installmentGroup: ['alfa-bnpl', 'split'];
      creditGroup: ['credit-online-short'];
      sberInstallmentGroup: ['sber-installment'];
    };
    thankYouPageControls?: {
      isDiscountAvailable: boolean;
      isFinalPriceAvailable: boolean;
      availablePaymentTypes: string[];
    };
    insurancePreProductText?: string;
    vsdaValidateErrorPopup?: {
      title: string;
      description: string;
      imgUrl: string;
      buttonText: string;
      deliveryTypes: DeliverySlug;
    };
    badReserved: boolean;
    onlineDeliveryTexts?: {
      title: string;
      subtitle: string;
      error: string;
      subtitleChangePhone: string;
      buttonPhoneSend: string;
      buttonPhoneChange: string;
    };
    phoneConfirmTexts: {
      title: string;
      subtitle: string;
      linkName: string;
    };
    ctnNotificationText: string;
    isCtnNiceClassAvailable: boolean;
    vsdaApproveSettings: {
      deliveryTypes: string[];
      approveApplicationStatus: string;
      maxAttempts: number;
      delay: number;
    };
    vdPostMessages: Record<string, string>;
    phoneNumber?: string;
    BNPL_icon?: string;
    BNPL_icon_active?: string;
    installmentDefaultSettings: {
      period: string;
      periodId: string;
    };
    unknownEmail?: string;
    unknownFirstName: string;
    isOnlyFreeInstallment: boolean;
    isNewDesign: boolean;
    isChangeRegionInBundle: boolean;
    isChangeTariffInBundle: boolean;
    sber_installment: boolean;
    simDeliveryAfterPDenable?: boolean;
    withoutRoutesV1?: boolean;
    isPromoEsimDisabled?: boolean;
    fastSim: {
      enabled: boolean;
      regions: number[];
    };
    isShowEsim: boolean;
    isShowEsimForMNP: boolean;
    isChoiceEsimDefault: boolean;
    isChoiceEsimOrPlastic: boolean;

    withoutPresets: {
      enabled: boolean;
      tariff: WithoutPresetsTariffType[];
    };
    vsdaCloseFrameSettings: {
      closeMessage: string;
      delay: number;
      deliveryTypes: string[];
      defaultDelay: number;
    };
    posCreditError: {
      paymentStatusHeadline: string;
      paymentStatusDescription: string;
      paymentStatusButton: string;
    };
    promotionalSim: {
      enabled: boolean;
      dpcId: number[];
    };
    deliveryOnlyWithAddress: boolean;
    preOrderInfoText?: string;
    sendSmsPaymentTypes: string[];
  };

  export type FeatureFlags = OnlyBoolKeys<QPBasketShop>;
  export type FeatureSettings = OnlyNotBoolKeys<QPBasketShop>;

  export type QPBnplCommon = {
    todayPaymentTextNew?: string;
    checkoutPaymentText: string;
    paymentStatusHeadline: string;
    paymentStatusDescription: string;
    paymentStatusButton: string;
    paymentsPeriod?: string;
  };

  export type QPBnplTexts = {
    serviceInfo: string;
    checkoutButtonText: string;
    paymentInfo?: string;
    shortInfoNew?: string;
    serviceInfoNew?: string;
  };

  export type QPBnpl = {
    common: QPBnplCommon;
    bySlug: Partial<Record<BnplType, QPBnplTexts>>;
  };

  export type QPPvzPartnerImg = {
    partnerIcon: string;
    pointIcon: string;
  };

  export type QPTexts = {
    orderPage: {
      title: string;
      checkoutButton: string;
      checkoutButtonB2B: string;
      eSim: {
        eSimButtonText: string;
        eSimRadiobuttonTitle: string;
        eSimRadiobuttonDescription: string;
        simRadiobuttonTitle: string;
        simRadiobuttonDescription: string;
        eSimSlugStatus: string;
      };
      pvzIcons: Record<string, QPPvzPartnerImg>;
      fastSimButtonText: string;
      b2bModal: {
        b2bModalTitle: string;
        b2bModalSubtitle: string;
        b2bModalResult: string;
        b2bModalAcceptButton: string;
        b2bModalDeclineButton: string;
      };
      somethingWentWrongModal: {
        title: string;
        description: string;
        label: string;
        primaryButtonText: string;
        secondaryButtonText: string;
      };
      mnpRegionErrorModal: {
        title: string;
        description: string;
        phoneInputLabel: string;
        phoneInputPlaceholder: string;
        label: string;
        placeholder: string;
        primaryButtonText: string;
        secondaryButtonText: string;
      };
      mnpRegionPreviewModal: {
        simpleTitle: string;
        complexTitle: string;
        subtitle: string;
        buttonText: string;
      };
      mnpComplexRegionsModal: {
        title: string;
        description: string;
        complexRegions: TComplexRegion;
      };
      existInBeelineMnpModal: {
        title: string;
        description: string;
        label: string;
        primaryButtonText: string;
        secondaryButtonText: string;
        phoneInputLabel: string;
      };
      hasApplicationMnpModal: {
        title: string;
        description: string;
        label: string;
        primaryButtonText: string;
        phoneInputLabel: string;
      };
      isLessSixtyDaysMnpModal: {
        title: string;
        description: string;
        label: string;
        primaryButtonText: string;
        phoneInputLabel: string;
      };
      mnpItemError: {
        unknownErrorItemTitle: string;
        unknownErrorItemSubTitle: string;
        unknownErrorButtonTitle: string;
        phoneErrorItemTitle: string;
        phoneErrorItemSubTitle: string;
        phoneErrorButtonTitle: string;
        regionErrorItemTitle: string;
        regionErrorItemSubTitle: string;
        regionErrorButtonESimTitle: string;
        regionErrorButtonESimSubTitle: string;
        regionErrorButtonGeoTitle: string;
        regionErrorButtonGeoSubTitle: string;
        regionErrorButtonOfficeTitle: string;
        regionErrorButtonOfficeSubTitle: string;
      };
    };
    sidebar: {
      phoneNumberError: string;
      phoneNumberLabel: string;
      phoneNumberPlaceholder: string;
      finalPrice: string;
      recall: string;
    };
    deliveryPage: {
      authGroupTitles: {
        esiaGroupTitle: string;
        esiaGroupSubTitle: string;
        receiptGroupTitle: string;
        receiptGroupSubTitle: string;
        receiptGroupItemPassportTitle: string;
        receiptGroupItemPassportText: string;
        receiptGroupItemSamoregTitle: string;
        receiptGroupItemSamoregText: string;
        receiptGroupItemSamoregLinkText: string;
      };
      authModals: {
        esiaNonResidentNotification: {
          title: string;
          mainText: string;
          buttonText: string;
        };
        esiaNonResidentTakeawayNotification: {
          title: string;
          mainText: string;
        };
      };
      deliveryTooltipText: string;
      deliveryMethodTitle: string;
      deliveryMethodTitleOnlyCourier: string;
      deliveryMethodTitleOnlyTakaway: string;
      deliveryMethodTitleOnlineDelivery: string;
      paymentMethodTitle: string;
      authMethodTitle: string;
      takeawayButton: string;
      takeawayButtonChange: string;
      takeawayPvzButtonChange: string;
      takeawayBeelineOffice: string;
      takeawayHereButton: string;
      courierPickButton: string;
      B2BcourierPickButton: string;
      preorderPickButton: string;
      courierChangeButton: string;
      paymentGroupTitles: {
        receiptGroup: string;
        cardOnlineGroup: string;
        sbpGroup: string;
        installmentGroup: string;
        creditGroup: string;
        sberInstallmentGroup: string;
      };
      paymentGroupImgs?: Record<string, string>;
      cashPaymentButton: string;
      cardPaymentButton: string;
      loanPaymentButton: string;
      installmentPaymentButton: string;
      SBPPaymentButton: string;
      courier: {
        title: string;
        cityLabel: string;
        streetLabel: string;
        flatLabel: string;
        dateLabel: string;
        costFree: string;
        submit: string;
        streetPlaceholder: string;
        flatPlaceholder: string;
      };
      b2b: {
        formTitle: string;
        formSubTitle: string;
        formInnInputTitle: string;
        formInnInputPlaceholder: string;
        formInnInputError: string;
        formCompanyNameInputTitle: string;
        formCompanyNameInputPlaceholder: string;
        formCompanyNameInputError: string;
      };
      personContacts: {
        firstNameTitle: string;
        firstNamePlaceholder: string;
        emailTitle: string;
        emailPlaceholder: string;
        emailAdvAgreementTitle: string;
      };
      paymentByCardPageTitle: string;
      sbpPageTitle: string;
      sbpPageSubtitle: string;
    };
    emptyBasketPage: {
      imgUrl: string;
      title: string;
      subtitle: string;
      toCatalogPageText: string;
      toHomePageText: string;
    };
    paymentPage: {
      sbpPageTitle: string;
      sbpPageSubtitle: string;
      paymentModalError: {
        title: string;
        description: string;
        button: string;
      };
      paymentByCardPageTitle: string;
    };
    promoCodeBlockTexts: {
      sidebarPromoInfo: string;
      inputPlaceholder: string;
      label: string;
      caption: string;
      error?: string;
      errorCaptcha?: string;
    };
    credit: {
      creditOrInstallment: {
        title: string;
        subtitle: string;
      };
    };
    errorModal: {
      title: string;
      description: string;
    };
    credit_block: {
      willSendRequest: {
        label: string;
        forLink: string;
      };
    };
    credit_popup_content: IPopupContentSiteTexts;
    insurance: {
      blockTitle: string;
      installmentInsuranceBlockTitle: string;
      periodPreText: string;
      preProductText: string;
      changeButton: string;
      emptyTitle: string;
      emptyDescription: string;
      emptyPeriodDescription: string;
      insuranceModal: {
        description: string;
        title: string;
        detailText: string;
        detailTextShort: string;
        recommended: string;
      };
      insurancesInfo: IServiceText[];
    };
    paymentGroupTitles: {
      receiptGroup: string;
      cardOnlineGroup: string;
      sbpGroup: string;
      installmentGroup: string;
      creditGroup: string;
      sberInstallmentGroup: string;
    };
    prettyCtn: {
      ctnCategoryTitleByAlias: {
        spetsialnyie: string;
        zolotie: string;
        serebryanie: string;
        bronzovie: string;
        platinum: string;
        goldpremium: string;
        goldvip: string;
      };
    };
    common: {
      regionWarning: {
        title: string;
        description: string;
        button: string;
      };
      regionTariffWarning: {
        title: string;
        description: string;
        button: string;
      };
      regionCtnWarning: {
        title: string;
        description: string;
        button: string;
      };
      outOfStock: {
        title: string;
        description: string;
        button: string;
      };
    };
  };

  export type QPSberSteps = {
    checkoutPaymentText: string;
    checkoutButtonText: string;
    subtitleForPdForm?: string;
    legalForPdForm?: string;
  };

  export type QPSberPopup = {
    title: string;
    subTitle: string;
    creditInfo: string;
    creditTitle: string;
    installmentTitle: string;
    installmentWithInsuranceTitle: string;
    installmentDescription: string;
    creditDescription: string;
    serviceInfo: string;
    installmentButtonText: string;
    creditButtonText: string;
    fullCostOfLoanText: string;
    fullCostOfLoan: string;
    defaultMonth: number | null;
  };

  export type QPSberError = {
    paymentStatusHeadline: string;
    paymentStatusDescription: string;
    paymentStatusButton: string;
  };

  export type QPSberInstallment = {
    checkoutStepTwo: QPSberSteps;
    popUp: QPSberPopup;
    errorPopUp: QPSberError;
  };
}
